import * as React from "react"

import { cn } from "@/lib/utils"
import { Badge } from "@/shadcn/components/ui/badge"

import { header as labels, resultTallyParts, verdicts } from "./copy"
import { countFindings, VERDICT_TONE } from "./severity"
import type { MedicResult } from "./types"

/**
 * Padded separator between secondary values.
 *
 * A `·` joined into a string sits tight against the digits either side at this
 * size; giving it real horizontal padding and dropping its contrast is what
 * actually separates the values. aria-hidden so it isn't announced as "middot".
 */
function Sep() {
  return (
    <span aria-hidden="true" className="tw:px-1.5 tw:text-muted-foreground/50">
      ·
    </span>
  )
}

/**
 * The report's summary: the verdict on the left, the run's secondary facts on
 * the right, in one row.
 *
 * One row rather than a stack. As two lines the counts and the timestamp read as
 * one paragraph despite being different kinds of information — a status summary
 * and a timestamp — and the second line was left orphaned under the first with
 * no divider to mark the change of register.
 *
 * Weight follows a single rule here: the verdict message is the only primary
 * statement, so it alone gets foreground colour and medium weight. Everything
 * else — counts, timestamp, its label — is secondary metadata and shares one
 * muted, normal-weight treatment. Previously the timestamp's value was bolded
 * while the counts beside it were not, which implied a distinction that wasn't
 * there.
 */
export default function MedicVerdictHeader({
  result,
  renderRanAt,
  runState,
}: {
  result: MedicResult
  /* Injected: the panel renders timestamps through the app's DateDisplay, which
   * needs the user's timezone from Redux; the share page has no user context. */
  renderRanAt?: () => React.ReactNode
  /*
   * Set only when the run is in flight AND no progress bar is being drawn, so
   * this is the sole thing able to say what is happening. The caller owns that
   * condition because it is the one holding both facts.
   */
  runState?: 'queued' | 'running'
}) {
  /*
   * A verdict is only written on terminal close, so its absence means the run is
   * still going. The badge and message are then suppressed: the progress bar
   * above already names the phase and percentage, and repeating "Checking this
   * feed…" beside a moving bar and a "Medic Running…" button states one fact
   * three times. The tally still earns its place — it says what has been learned
   * so far. The share page only renders terminal runs, so it always gets the badge.
   *
   * When there is no progress bar, though, that suppression left this card as an
   * empty bordered box containing only a timestamp — which is exactly the state
   * where the reader most needs telling what is going on. `runState` fills in
   * then, and only then.
   */
  const verdict = result.verdict
  const state = verdict ?? (runState === 'queued' ? 'queued' : runState === 'running' ? 'pending' : undefined)
  const copy = state ? verdicts[state] : undefined
  const tally = resultTallyParts(countFindings(result))
  const ranAt = renderRanAt?.()

  if (!copy && tally.length === 0 && !ranAt) return null

  return (
    <div className="tw:flex tw:flex-col tw:gap-2 tw:rounded-xl tw:border tw:border-border tw:bg-muted/30 tw:px-4 tw:py-3">
      {/* State and secondary facts share the top line; the interpretation of that
          state gets its own, so it reads as a sentence rather than a caption
          wedged between a badge and a row of counts. */}
      {(state || tally.length > 0 || ranAt) && (
        <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-x-4 tw:gap-y-1.5">
          {state && copy ? (
            <Badge
              variant="outline"
              className={cn(
                "tw:h-6 tw:px-2.5 tw:text-xs tw:tracking-wide",
                VERDICT_TONE[state]
              )}
            >
              {copy.label}
            </Badge>
          ) : (
            <span />
          )}

          {(tally.length > 0 || ranAt) && (
            <div className="tw:flex tw:items-center tw:text-sm tw:text-muted-foreground tw:tabular-nums">
              {tally.map((part, idx) => (
                <React.Fragment key={part}>
                  {idx > 0 && <Sep />}
                  <span>{part}</span>
                </React.Fragment>
              ))}
              {ranAt && (
                <>
                  {tally.length > 0 && <Sep />}
                  <span>
                    {labels.ranLabel} {ranAt}
                  </span>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {state && copy && (
        <p className="tw:text-sm tw:leading-5 tw:font-medium tw:text-foreground">
          {copy.message}
        </p>
      )}
    </div>
  )
}
